import { inject, Injectable, signal } from '@angular/core';
import { JsonApiClientService, ResourceTypes as JsonApiResourceTypes } from '@big-direkt/json-api-client';
import { type MenuModel } from '@big-direkt/utils/shared';
import { firstValueFrom, Observable } from 'rxjs';
import { FooterMenuModel } from '../models/footer-menu.model';
import { HeaderMenuModel } from '../models/header-menu.model';
import { FooterMenuTypes, HeaderMenuTypes } from '../models/menu.types';

@Injectable({
    providedIn: 'root',
})
export class NavService {
    private readonly jsonApiClientService = inject(JsonApiClientService);

    public readonly headerMenus = signal<HeaderMenuModel | undefined>(undefined);
    public readonly footerMenus = signal<FooterMenuModel | undefined>(undefined);

    public async initHeaderMenus(menus: { name: keyof HeaderMenuModel; type: HeaderMenuTypes }[]): Promise<void> {
        this.headerMenus.set((await this.getMenus<HeaderMenuModel>(menus)));
    }

    public async initFooterMenus(menus: { name: keyof FooterMenuModel; type: FooterMenuTypes }[]): Promise<void> {
        this.footerMenus.set((await this.getMenus<FooterMenuModel>(menus)));
    }

    private async getMenus<T extends FooterMenuModel | HeaderMenuModel>(
        menus: {
            name: keyof T;
            type: T extends FooterMenuModel ? FooterMenuTypes : HeaderMenuTypes;
        }[],
    ): Promise<T> {
        const observables: Observable<MenuModel>[] = [];

        menus.forEach(menu => {
            observables.push(this.jsonApiClientService.load(menu.type as FooterMenuTypes & HeaderMenuTypes, JsonApiResourceTypes.Menu));
        });

        const resolvedMenus: Partial<Record<keyof T, MenuModel>> = {} as Partial<Record<keyof T, MenuModel>>;

        try {
            const results = await Promise.allSettled(observables.map(o => firstValueFrom(o)));
            results.forEach((result, idx) => {
                const menu = menus[idx];
                if (result.status === 'fulfilled') {
                    resolvedMenus[menu.name] = result.value;
                } else {
                    // eslint-disable-next-line no-console
                    console.error('not able to load menu', menu.name);
                }
            });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars,  sonarjs/no-ignored-exceptions
        } catch (_e: unknown) {
            // no-op: individual errors handled above
        }

        return resolvedMenus as T;
    }
}
